import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { PackageCheck, CheckCircle2, ArrowLeft, ScanLine } from 'lucide-react';
import Button from '../../components/ui/Button';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import { parseLotScan, scanMatches } from '../../utils/lotScan';
import { rowLabel } from '../../utils/nurseryRowSlots';
import nurseryService from '../../services/nursery';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { PickItemCard } from '../../components/nursery/picking/PickItemCard';
import { LOAD_TYPE_LABELS, type PickItem, type PickList } from '../../types/nursery';

/**
 * /nursery/picking/:id — one load's pick list.
 *
 * A collections load is grouped by customer, earliest booking first: the
 * person at the counter was booked first. Every other load is grouped by
 * where the trees stand, in walk order, so a picker goes down each row once.
 * Anything not at the despatch farm sinks to the bottom, flagged.
 */

interface Section {
  key: string;
  title: string;
  subtitle?: string;
  items: PickItem[];
}

function sectionsFor(list: PickList, tz: string): Section[] {
  const out: Section[] = [];
  const byKey = new Map<string, Section>();
  const collection = list.load.load_type === 'collection';
  for (const it of list.items) {
    let key: string;
    let title: string;
    let subtitle: string | undefined;
    if (collection) {
      key = `order:${it.order_id}`;
      title = it.customer_name ?? it.order_number;
      subtitle = [it.order_number, it.collection_at ? `collecting ${formatDateTimeInTz(it.collection_at, tz)}` : null].filter(Boolean).join(' · ');
    } else if (!it.at_despatch_farm) {
      key = 'elsewhere';
      title = 'Not at this farm yet';
    } else {
      key = `loc:${it.block_name ?? ''}:${it.row_number ?? it.location_name ?? ''}:${it.row_side ?? ''}`;
      title = [it.block_name, it.row_number != null ? rowLabel(it.row_number, it.row_side) : null].filter(Boolean).join(' · ') || it.location_name || 'No location recorded';
    }
    let s = byKey.get(key);
    if (!s) {
      s = { key, title, subtitle, items: [] };
      byKey.set(key, s);
      out.push(s);
    }
    s.items.push(it);
  }
  return out;
}

function PickListPage() {
  const { id } = useParams<{ id: string }>();
  const loadId = Number(id);
  const tz = useOrgTimezone();
  const [list, setList] = useState<PickList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState<number | null>(null);
  const [scanNote, setScanNote] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    try {
      const res = await nurseryService.getPickList(loadId);
      if (res.success) setList(res.data);
      else setError(errorMessage(res, 'Could not load the pick list'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the pick list'));
    }
  }, [loadId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const sections = useMemo(() => (list ? sectionsFor(list, tz) : []), [list, tz]);

  /**
   * A scanned lot label finds that lot on the list: the first item still to
   * pick from it is highlighted and scrolled to. A lot not on this load says
   * so, rather than leaving the picker wondering whether the scan worked.
   */
  const onScan = (raw: string) => {
    setScanning(false);
    const scan = parseLotScan(raw);
    if (!scan || !list) return;
    const matches = list.items.filter((i) => scanMatches(scan, { id: i.lot_id, code: i.lot_code }));
    if (!matches.length) {
      setFound(null);
      setScanNote(`${'lotId' in scan ? `Lot #${scan.lotId}` : scan.code} is not on this load.`);
      return;
    }
    const target = matches.find((i) => !i.pick) ?? matches[0];
    setFound(target.lot_id);
    setScanNote(`${matches.length} item${matches.length === 1 ? '' : 's'} on this load ${matches.length === 1 ? 'is' : 'are'} from this lot.`);
    requestAnimationFrame(() => document.getElementById(`pick-${target.allocation_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  if (!list) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        {error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <Link to="/nursery/picking" className="text-sm text-primary-700 underline">Back to picking</Link>
          </div>
        ) : <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />}
      </div>
    );
  }

  const { load, progress } = list;
  const editable = ['released', 'picking', 'loaded'].includes(load.status);
  const done = progress.items > 0 && progress.resolved >= progress.items;
  const pct = progress.items ? Math.round((progress.resolved / progress.items) * 100) : 0;

  return (
    <>
      <PageHeader
        title={LOAD_TYPE_LABELS[load.load_type]}
        subtitle={`${load.load_number} · ${formatLocalDateInTz(load.despatch_date, tz)} · ${load.despatch_farm_name}`}
        icon={<PackageCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        <Link to="/nursery/picking" className="inline-flex items-center gap-1 text-sm text-primary-700">
          <ArrowLeft className="h-4 w-4" /> All loads
        </Link>

        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-neutral-800">
              {progress.resolved} of {progress.items} items · {progress.picked_trees.toLocaleString()} of {progress.trees.toLocaleString()} trees
            </span>
            <LoadStatusBadge status={load.status} />
          </div>
          <div className="mt-2 h-2.5 rounded-full bg-neutral-100 overflow-hidden">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {(load.vehicle || load.driver_name || load.notes) && (
            <p className="text-xs text-neutral-600 mt-2 break-words whitespace-pre-line">
              {[load.vehicle, load.driver_name].filter(Boolean).join(' · ')}
              {load.notes ? `${load.vehicle || load.driver_name ? '\n' : ''}${load.notes}` : ''}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setScanNote(null); setScanning(true); }}>
            <ScanLine className="h-4 w-4" /> Scan a lot label
          </Button>
          {scanNote && <span className="text-sm text-neutral-700 break-words min-w-0">{scanNote}</span>}
        </div>

        {done && (
          <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
            <span>Everything on this load has been picked or accounted for.</span>
          </div>
        )}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {sections.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-8">Nothing on this load has stock allocated to it yet.</p>
        ) : sections.map((s) => (
          <section key={s.key} aria-label={s.title}>
            <h2 className="text-sm font-semibold text-neutral-700 break-words">{s.title}</h2>
            {s.subtitle && <p className="text-xs text-neutral-500 break-words">{s.subtitle}</p>}
            <ul className="mt-2 space-y-3">
              {s.items.map((it) => (
                <PickItemCard
                  key={it.allocation_id}
                  item={it}
                  loadId={load.id}
                  despatchFarmName={load.despatch_farm_name}
                  timezone={tz}
                  editable={editable}
                  onChanged={setList}
                  highlighted={found === it.lot_id}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
      {scanning && <BarcodeScanner label="Scan a lot label" onScan={onScan} onClose={() => setScanning(false)} />}
    </>
  );
}

export default PickListPage;
